'use client';

import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import {
  Mail, MapPin, Phone, Target, Sparkles,
  Link as LinkIcon, Star, Calendar, StickyNote,
  CheckSquare, FileText, Pencil, Trash2,
  Briefcase, GraduationCap, Inbox, BadgeCheck,
  Building2, UserCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/forms/button';
import { Textarea } from '@/components/ui/forms/textarea';
import { PanelDialog } from '@/components/ui/layout/PanelDialog';
import {
  SectionPanel,
  StatRow,
  Chip,
  HintNote,
  IconLabel,
  MetaRow,
  EntityTitle,
  EntitySubtitle,
  PlaceholderSection,
  SimpleKeyValueTable,
} from '@/components/ui/design-system';
import type { Contact, RelationshipTask, Interview } from '@/types/report-types';
import type { UserProfile } from '@/actions/ContactsLookup';
import { AddNoteDialog } from '@/app/dashboard/components/outreach/actions/AddNoteDialog';
import { AddTaskDialog } from '@/app/dashboard/components/outreach/actions/AddTaskDialog';
import { ScheduleInterviewDialog } from '@/app/dashboard/components/outreach/actions/ScheduleInterviewDialog';
import { EmailCopy } from '@/components/common/EmailCopy';
import { CopyButton } from '@/components/common/CopyButton';
import { OutreachPriorityBadge } from '@/app/dashboard/components/contact/OutreachPriorityBadge';
import { useEventTracking } from '@/hooks/useEventTracking';
import { EventType } from '@/api/types/events';

interface ContactQuickViewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: Contact | null;
  profileData?: UserProfile | null;
  onReachOut?: (contact: Contact) => void | Promise<void>;
}

type OutreachMessage = {
  id: string;
  subject: string | null;
  content: string | null;
  direction: 'INBOUND' | 'OUTBOUND' | 'INTERNAL';
  created_at: string;
  ai_classification?: { type?: string } | null;
};

type OutreachThread = {
  id: string;
  contact?: { id: string } | null;
  messages?: OutreachMessage[];
};

type RelationshipPayload = {
  playbookId?: string;
  playbookName?: string;
  tasks?: RelationshipTask[];
  interviews?: Interview[];
};

type GenericRecord = Record<string, unknown>;

function getRecordArray(value: unknown): GenericRecord[] {
  return Array.isArray(value) ? value.filter((item): item is GenericRecord => !!item && typeof item === 'object') : [];
}

function getStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0) : [];
}

function getListEntries(value: unknown): unknown[] {
  return Array.isArray(value) ? value.filter((item) => item !== null && item !== undefined) : [];
}

function formatDateUs(value: string | number | Date | null | undefined): string {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '';
    return value.toLocaleDateString('en-US');
  }
  if (typeof value === 'number') {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US');
  }
  const normalized = value.trim();
  if (!normalized) return '';
  const isoDateMatch = normalized.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDateMatch) {
    const year = Number.parseInt(isoDateMatch[1], 10);
    const month = Number.parseInt(isoDateMatch[2], 10);
    const day = Number.parseInt(isoDateMatch[3], 10);
    if (Number.isFinite(year) && Number.isFinite(month) && Number.isFinite(day) && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', { timeZone: 'UTC' });
    }
  }
  const parsed = Date.parse(normalized);
  if (Number.isNaN(parsed)) return normalized;
  return new Date(parsed).toLocaleDateString('en-US');
}

function formatDateRange(start?: string | null, end?: string | null, isCurrent?: boolean): string {
  const from = formatDateUs(start) || 'Unknown start';
  if (isCurrent) return `${from} – Present`;
  return `${from} – ${formatDateUs(end) || 'Unknown end'}`;
}

function formatPercentValue(value: unknown): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return `${Math.max(0, Math.min(100, Math.round(value)))}%`;
}

function renderRecordValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map((entry) => renderRecordValue(entry)).join(', ');
  try { return JSON.stringify(value); } catch { return '—'; }
}

const experienceTitleKeys = ['title', 'job_title', 'position', 'role'];
const experienceCompanyKeys = ['company_name', 'company', 'employer', 'organization'];
const experienceLocationKeys = ['locality', 'location', 'area', 'region'];
const experienceStartKeys = ['start_date', 'start', 'startDate', 'from'];
const experienceEndKeys = ['end_date', 'end', 'endDate', 'to'];
const experienceCurrentKeys = ['is_current', 'current', 'isCurrent'];
const experienceCompletenessKeys = Array.from(new Set([...experienceTitleKeys, ...experienceCompanyKeys, ...experienceLocationKeys, ...experienceStartKeys, ...experienceEndKeys, ...experienceCurrentKeys, 'summary']));

function normalizeExperienceText(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.trim().toLowerCase().replace(/\s+/g, ' ');
}

function getFirstExperienceString(record: GenericRecord, keys: string[]): string {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim().length > 0) return value.trim();
  }
  return '';
}

function getNormalizedExperienceText(record: GenericRecord, keys: string[]): string {
  return normalizeExperienceText(getFirstExperienceString(record, keys));
}

function normalizeExperienceDate(record: GenericRecord, keys: string[]): string {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim().length > 0) return value.trim().toLowerCase();
  }
  return '';
}

function toExperienceTimestamp(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const normalized = value.trim();
    if (!normalized) return null;
    const parsed = Date.parse(normalized);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return null;
}

function getExperienceTimestamp(record: GenericRecord, keys: string[]): number | null {
  for (const key of keys) {
    const timestamp = toExperienceTimestamp(record[key]);
    if (timestamp !== null) return timestamp;
  }
  return null;
}

function getExperienceBoolean(record: GenericRecord, keys: string[]): boolean {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (normalized === 'true') return true;
      if (normalized === 'false') return false;
    }
  }
  return false;
}

function buildExperienceDedupKey(record: GenericRecord): string {
  return [
    getNormalizedExperienceText(record, experienceTitleKeys),
    getNormalizedExperienceText(record, experienceCompanyKeys),
    getNormalizedExperienceText(record, experienceLocationKeys),
    normalizeExperienceDate(record, experienceStartKeys),
    normalizeExperienceDate(record, experienceEndKeys),
  ].join('|');
}

function computeExperienceCompletenessScore(record: GenericRecord): number {
  return experienceCompletenessKeys.reduce((score, key) => {
    const value = record[key];
    if (typeof value === 'string' && value.trim().length > 0) return score + 1;
    if (typeof value === 'number' || typeof value === 'boolean') return score + 1;
    return score;
  }, 0);
}

function chooseMoreCompleteExperience(existing: GenericRecord, candidate: GenericRecord): GenericRecord {
  return computeExperienceCompletenessScore(candidate) > computeExperienceCompletenessScore(existing) ? candidate : existing;
}

function sortExperienceRecords(records: GenericRecord[]): GenericRecord[] {
  return [...records].sort((a, b) => {
    const aStart = getExperienceTimestamp(a, experienceStartKeys);
    const bStart = getExperienceTimestamp(b, experienceStartKeys);
    const startDiff = (bStart ?? Number.NEGATIVE_INFINITY) - (aStart ?? Number.NEGATIVE_INFINITY);
    if (startDiff !== 0) return startDiff;
    const aCurrent = getExperienceBoolean(a, experienceCurrentKeys);
    const bCurrent = getExperienceBoolean(b, experienceCurrentKeys);
    if (aCurrent !== bCurrent) return aCurrent ? -1 : 1;
    const aEnd = getExperienceTimestamp(a, experienceEndKeys);
    const bEnd = getExperienceTimestamp(b, experienceEndKeys);
    const endDiff = (bEnd ?? Number.NEGATIVE_INFINITY) - (aEnd ?? Number.NEGATIVE_INFINITY);
    if (endDiff !== 0) return endDiff;
    const aCompany = getNormalizedExperienceText(a, experienceCompanyKeys);
    const bCompany = getNormalizedExperienceText(b, experienceCompanyKeys);
    if (aCompany !== bCompany) return aCompany.localeCompare(bCompany);
    const aTitle = getNormalizedExperienceText(a, experienceTitleKeys);
    const bTitle = getNormalizedExperienceText(b, experienceTitleKeys);
    if (aTitle !== bTitle) return aTitle.localeCompare(bTitle);
    return 0;
  });
}

function deduplicateAndSortExperienceRecords(records: GenericRecord[]): GenericRecord[] {
  if (records.length === 0) return [];
  const seen = new Map<string, GenericRecord>();
  for (const record of records) {
    const key = buildExperienceDedupKey(record);
    const existing = seen.get(key);
    seen.set(key, existing ? chooseMoreCompleteExperience(existing, record) : record);
  }
  return sortExperienceRecords(Array.from(seen.values()));
}

export function ContactQuickViewDialog({
  open,
  onOpenChange,
  contact,
  profileData = null,
  onReachOut,
}: ContactQuickViewDialogProps) {
  const { trackEvent } = useEventTracking();
  const [isLoading, setIsLoading] = useState(false);
  const [relationship, setRelationship] = useState<RelationshipPayload | null>(null);
  const [threads, setThreads] = useState<OutreachThread[]>([]);
  const [notesThread, setNotesThread] = useState<OutreachThread | null>(null);
  const [dataError, setDataError] = useState<string | null>(null);
  const [isAddNoteOpen, setIsAddNoteOpen] = useState(false);
  const [isAddTaskOpen, setIsAddTaskOpen] = useState(false);
  const [isScheduleInterviewOpen, setIsScheduleInterviewOpen] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteContent, setEditingNoteContent] = useState('');
  const [isSavingNoteEdit, setIsSavingNoteEdit] = useState(false);
  const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !contact) return;
    let isActive = true;

    const fetchData = async () => {
      setIsLoading(true);
      setDataError(null);
      try {
        const [hubRes, convoRes, notesRes] = await Promise.all([
          fetch(`/api/hub/contacts?contactIds=${contact.id}`, { cache: 'no-store' }),
          fetch('/api/outreach/conversations', { cache: 'no-store' }),
          fetch(`/api/conversations?thread_type=NOTES&contact_id=${contact.id}`, { cache: 'no-store' }),
        ]);
        if (!hubRes.ok) throw new Error('Failed to load contact history');
        if (!convoRes.ok) throw new Error('Failed to load engagement history');
        if (!notesRes.ok) throw new Error('Failed to load notes');

        const hubData = (await hubRes.json()) as { relationships?: RelationshipPayload[] };
        const convoData = (await convoRes.json()) as { conversations?: OutreachThread[] };
        const notesData = (await notesRes.json()) as { threads?: OutreachThread[] };

        if (!isActive) return;
        setRelationship(hubData.relationships?.[0] ?? null);
        setThreads(convoData.conversations ?? []);

        const notesThreadId = notesData.threads?.[0]?.id;
        if (notesThreadId) {
          const notesDetailRes = await fetch(`/api/outreach/conversations/${notesThreadId}`, { cache: 'no-store' });
          if (notesDetailRes.ok) {
            const detail = (await notesDetailRes.json()) as { conversation?: OutreachThread };
            setNotesThread(detail.conversation ?? null);
          } else {
            setNotesThread(null);
          }
        } else {
          setNotesThread(null);
        }
      } catch (error) {
        if (!isActive) return;
        setDataError(error instanceof Error ? error.message : 'Failed to load quick view data');
      } finally {
        if (isActive) setIsLoading(false);
      }
    };

    void fetchData();
    return () => { isActive = false; };
  }, [open, contact]);

  const threadForContact = useMemo(() => {
    if (!contact) return null;
    return threads.find((thread) => thread.contact?.id === contact.id) ?? null;
  }, [threads, contact]);

  const engagementMessages = useMemo(() => {
    return (threadForContact?.messages ?? [])
      .filter((msg) => msg.direction !== 'INTERNAL')
      .slice().reverse().slice(0, 6);
  }, [threadForContact]);

  const noteMessages = useMemo(() => (notesThread?.messages ?? []).slice().reverse(), [notesThread]);

  const reportId = relationship?.playbookId || contact?.playbookId;
  const profileRecord = ((profileData ?? contact) ?? null) as unknown as GenericRecord | null;

  const profileEmails = useMemo(() => {
    const direct = getStringArray(profileData?.emails);
    const structured = getRecordArray(profileData?.email_addresses)
      .map((entry) => (typeof entry.email === 'string' ? entry.email : null))
      .filter((email): email is string => !!email);
    const fallback = [...(contact?.email ? [contact.email] : []), ...(Array.isArray(contact?.emails) ? contact.emails : [])];
    return Array.from(new Set([...direct, ...structured, ...fallback]));
  }, [profileData, contact?.email, contact?.emails]);

  const profilePhones = useMemo(() => {
    const direct = getStringArray(profileData?.phones);
    const structured = getRecordArray(profileData?.phone_numbers)
      .map((entry) => (typeof entry.phone === 'string' ? entry.phone : null))
      .filter((phone): phone is string => !!phone);
    return Array.from(new Set([...direct, ...structured, ...(Array.isArray(contact?.phones) ? contact.phones : [])]));
  }, [profileData, contact?.phones]);

  const profileLinks = useMemo(() => {
    const links: string[] = [];
    if (contact?.linkedIn) links.push(contact.linkedIn);
    if (profileData?.url) links.push(profileData.url);
    return Array.from(new Set([...links, ...getStringArray(profileRecord?.websites), ...getStringArray(profileRecord?.social_links)]));
  }, [contact?.linkedIn, profileData?.url, profileRecord]);

  const linkedInUrl = useMemo(() => {
    if (!contact?.linkedIn) return null;
    const normalized = contact.linkedIn.trim();
    if (!normalized) return null;
    return /^https?:\/\//i.test(normalized) ? normalized : `https://${normalized}`;
  }, [contact?.linkedIn]);

  const profileExperience = useMemo(() => deduplicateAndSortExperienceRecords(getRecordArray(profileData?.experience ?? contact?.experience)), [profileData?.experience, contact?.experience]);
  const profileEducation = useMemo(() => getRecordArray(profileRecord?.education ?? contact?.education), [profileRecord, contact?.education]);
  const profileOtherLists = useMemo(() => {
    const listKeys = ['certifications', 'languages', 'projects', 'publications', 'courses', 'honors', 'volunteer'];
    return listKeys.map((key) => ({ key, values: getListEntries(profileRecord?.[key]) })).filter((e) => e.values.length > 0);
  }, [profileRecord]);

  const profileRawFields = useMemo(() => {
    if (!profileRecord) return [];
    const hidden = new Set(['emails', 'email_addresses', 'phones', 'phone_numbers', 'experience', 'education', 'certifications', 'languages', 'projects', 'publications', 'courses', 'honors', 'volunteer']);
    return Object.entries(profileRecord).filter(([, v]) => v !== null && v !== undefined).filter(([k]) => !hidden.has(k));
  }, [profileRecord]);

  const ensureNotesThreadId = async () => {
    if (notesThread?.id) return notesThread.id;
    const response = await fetch('/api/conversations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ thread_type: 'NOTES', contact_id: contact?.id }),
    });
    if (!response.ok) throw new Error('Failed to create notes thread');
    const data = await response.json();
    return data.thread?.id as string;
  };

  const handleSaveNote = async (content: string, type: 'general' | 'interview' | 'task' | 'follow_up') => {
    if (!contact) return;
    const threadId = await ensureNotesThreadId();
    const response = await fetch('/api/outreach/drafts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ conversationThreadId: threadId, contactId: contact.id, type: 'note', subject: `Note: ${type}`, body: content }),
    });
    if (!response.ok) { const error = await response.json(); throw new Error(error.error || 'Failed to save note'); }
    const notesDetailRes = await fetch(`/api/outreach/conversations/${threadId}`, { cache: 'no-store' });
    if (notesDetailRes.ok) {
      const detail = (await notesDetailRes.json()) as { conversation?: OutreachThread };
      setNotesThread(detail.conversation ?? null);
    }
  };

  const handleStartEditNote = (messageId: string, content: string | null) => {
    setEditingNoteId(messageId);
    setEditingNoteContent(content ?? '');
  };

  const handleCancelEditNote = () => { setEditingNoteId(null); setEditingNoteContent(''); };

  const handleSaveNoteEdit = async () => {
    if (!editingNoteId) return;
    setIsSavingNoteEdit(true);
    try {
      const response = await fetch('/api/outreach/drafts', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messageId: editingNoteId, body: editingNoteContent }),
      });
      if (!response.ok) { const error = await response.json(); throw new Error(error.error || 'Failed to update note'); }
      if (notesThread?.id) {
        const notesDetailRes = await fetch(`/api/outreach/conversations/${notesThread.id}`, { cache: 'no-store' });
        if (notesDetailRes.ok) setNotesThread(((await notesDetailRes.json()) as { conversation?: OutreachThread }).conversation ?? null);
      }
      handleCancelEditNote();
    } finally {
      setIsSavingNoteEdit(false);
    }
  };

  const handleDeleteNote = async (messageId: string) => {
    setDeletingNoteId(messageId);
    try {
      const response = await fetch('/api/outreach/drafts', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messageId }),
      });
      if (!response.ok) { const error = await response.json(); throw new Error(error.error || 'Failed to delete note'); }
      if (notesThread?.id) {
        const notesDetailRes = await fetch(`/api/outreach/conversations/${notesThread.id}`, { cache: 'no-store' });
        if (notesDetailRes.ok) setNotesThread(((await notesDetailRes.json()) as { conversation?: OutreachThread }).conversation ?? null);
      }
    } finally {
      setDeletingNoteId(null);
    }
  };

  const handleSaveTask = async (task: { title: string; description?: string; priority: 'LOW' | 'MEDIUM' | 'HIGH'; dueDate?: Date }) => {
    if (!contact) return;
    const response = await fetch('/api/tasks', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: task.title, description: task.description, priority: task.priority, dueDate: task.dueDate?.toISOString(), contactId: contact.id }),
    });
    if (!response.ok) { const error = await response.json(); throw new Error(error.error || 'Failed to create task'); }
    const hubRes = await fetch(`/api/hub/contacts?contactIds=${contact.id}`, { cache: 'no-store' });
    if (hubRes.ok) setRelationship(((await hubRes.json()) as { relationships?: RelationshipPayload[] }).relationships?.[0] ?? null);
  };

  const handleScheduleInterview = async (interview: { type: 'PHONE_SCREEN' | 'TECHNICAL' | 'BEHAVIORAL' | 'ONSITE' | 'FINAL'; scheduledAt: Date; duration: number; isRemote: boolean; location?: string; meetingLink?: string; interviewerNames: string[]; notes?: string }) => {
    if (!contact) return;
    const response = await fetch('/api/interviews', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contact_id: contact.id, company_id: contact.companyId, interview_type: interview.type, scheduled_at: interview.scheduledAt.toISOString(), duration_minutes: interview.duration, is_remote: interview.isRemote, location: interview.location, meeting_link: interview.meetingLink, interviewer_names: interview.interviewerNames, notes: interview.notes }),
    });
    if (!response.ok) { const error = await response.json(); throw new Error(error.error || 'Failed to schedule interview'); }
    const hubRes = await fetch(`/api/hub/contacts?contactIds=${contact.id}`, { cache: 'no-store' });
    if (hubRes.ok) setRelationship(((await hubRes.json()) as { relationships?: RelationshipPayload[] }).relationships?.[0] ?? null);
  };

  const handleReachOut = () => {
    if (!contact || !onReachOut) return;
    trackEvent({
      event_type: EventType.CLICK,
      element_id: 'contact-quick-view-reach-out',
      event_details: { action: 'reach_out_click', funnel: 'contact_outreach', source: 'contact_quick_view_dialog', contact_id: contact.id, contact_name: contact.fullName, company_name: contact.company, contact_title: contact.title },
    });
    onOpenChange(false);
    void onReachOut(contact);
  };

  if (!contact) return null;

  const tasks = relationship?.tasks ?? [];
  const interviews = relationship?.interviews ?? [];
  const channelCount = profileEmails.length + profilePhones.length + profileLinks.length;
  const hasScores =
    typeof contact.relevanceLikelihood === 'number'
    || typeof contact.responseLikelihood === 'number'
    || typeof contact.hiringImpactLikelihood === 'number';

  const showRelevanceReason =
    Boolean(contact.relevanceReason)
    && contact.relevanceReason?.trim().toLowerCase()
       !== `${contact.title ?? ''} at ${contact.company ?? ''}`.trim().toLowerCase();

  const addButton = (onClick: () => void) => (
    <Button size="sm" variant="outline" className="h-7 px-2.5 text-xs" onClick={onClick}>
      Add
    </Button>
  );

  return (
    <>
      <PanelDialog
        open={open}
        onOpenChange={onOpenChange}
        eyebrow="Contact"
        title={contact.fullName}
        desktopMaxWidth="sm:max-w-2xl"
        desktopHeight="sm:h-[min(92vh,_860px)]"
        footer={
          <div className={`flex gap-3 ${onReachOut ? '' : 'flex-row-reverse'}`}>
            <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>
              Close
            </Button>
            {onReachOut && (
              <Button className="flex-1" onClick={handleReachOut}>
                Reach Out
              </Button>
            )}
          </div>
        }
      >
        <div className="space-y-3">
          {/* Identity strip — mirrors brief's "Career Profile" row:
              square accent block + name + meta line of icon-labeled
              company / title / location, all on one line. */}
          <div className="flex items-center gap-3 border border-border bg-card px-3 py-3 sm:px-4" style={{ boxShadow: 'var(--shadow-warm-sm)' }}>
            <div className="flex h-10 w-10 shrink-0 items-center justify-center bg-[var(--accent-soft)]">
              <UserCircle className="h-5 w-5 text-primary" aria-hidden />
            </div>
            <div className="min-w-0 flex-1 space-y-0.5">
              <EntityTitle as="p" size="md" truncate>{contact.fullName}</EntityTitle>
              <MetaRow size="sm">
                {contact.company ? (
                  <IconLabel icon={Building2} size="sm">{contact.company}</IconLabel>
                ) : null}
                {contact.title ? (
                  <IconLabel icon={Briefcase} size="sm">{contact.title}</IconLabel>
                ) : null}
                {contact.location ? (
                  <IconLabel icon={MapPin} size="sm">{contact.location}</IconLabel>
                ) : null}
              </MetaRow>
            </div>
            {(contact.seniority || contact.isFavorite) ? (
              <div className="flex shrink-0 items-center gap-1.5">
                {contact.seniority ? <Chip variant="neutral">{contact.seniority}</Chip> : null}
                {contact.isFavorite ? (
                  <IconLabel icon={Star} tone="primary" size="sm" strong>Saved</IconLabel>
                ) : null}
              </div>
            ) : null}
          </div>

          {/* Tags row (if any) */}
          {contact.tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              {contact.tags.map((tag) => (
                <Chip key={tag} variant="outline">{tag}</Chip>
              ))}
            </div>
          )}

          {/* Why-this-contact callout — only show if it adds info beyond
              "Title at Company" which is already in the identity strip. */}
          {showRelevanceReason && (
            <HintNote icon={<Sparkles className="h-3.5 w-3.5 text-primary" aria-hidden />}>
              {contact.relevanceReason}
            </HintNote>
          )}

          {/* Reach — primary contact channels */}
          <SectionPanel title="Reach" icon={Inbox} contentClassName="divide-y divide-border">
            {contact.email ? (
              <div className="flex items-center justify-between gap-3 px-3 py-2.5 sm:px-4">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                  <EmailCopy email={contact.email} textClassName="text-body text-foreground truncate" iconClassName="h-3.5 w-3.5" showOnHover={false} />
                </div>
                {contact.emailSource && contact.emailSource !== 'unknown' ? (
                  <Chip variant={contact.emailSource === 'verified' ? 'primary' : 'outline'}>
                    {contact.emailSource}
                  </Chip>
                ) : null}
              </div>
            ) : null}
            {linkedInUrl ? (
              <div className="flex items-center gap-2 px-3 py-2.5 sm:px-4">
                <LinkIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                <Link
                  href={linkedInUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="min-w-0 flex-1 truncate text-body text-primary underline underline-offset-2"
                >
                  {linkedInUrl}
                </Link>
                <CopyButton text={linkedInUrl} title="Copy LinkedIn URL" successMessage="LinkedIn URL copied" showOnHover={false} className="shrink-0 p-0.5" iconClassName="h-3.5 w-3.5" />
              </div>
            ) : null}
            {!contact.email && !linkedInUrl ? (
              <PlaceholderSection
                icon={<Inbox className="h-6 w-6" />}
                title="No direct channels yet"
                description="Run a report to surface verified emails and LinkedIn profiles."
              />
            ) : null}
          </SectionPanel>

          {/* Quick stats: relevance + outreach priority + report link */}
          {(typeof contact.relevanceScore === 'number'
            || typeof contact.spotlightScore === 'number'
            || typeof contact.relevanceLikelihood === 'number'
            || typeof contact.responseLikelihood === 'number'
            || typeof contact.hiringImpactLikelihood === 'number'
            || reportId) && (
            <div className="flex flex-wrap items-center gap-2">
              {typeof contact.relevanceScore === 'number' ? (
                <StatRow
                  stats={[{ icon: Target, value: `${contact.relevanceScore}%`, label: 'Relevant' }]}
                />
              ) : null}
              {(typeof contact.spotlightScore === 'number'
                || typeof contact.relevanceLikelihood === 'number'
                || typeof contact.responseLikelihood === 'number'
                || typeof contact.hiringImpactLikelihood === 'number') ? (
                <OutreachPriorityBadge
                  spotlightScore={contact.spotlightScore}
                  relevanceLikelihood={contact.relevanceLikelihood}
                  responseLikelihood={contact.responseLikelihood}
                  hiringImpactLikelihood={contact.hiringImpactLikelihood}
                  badgeClassName="text-[10px] font-semibold uppercase tracking-[0.03em] px-2 py-0.5 bg-amber-100 text-amber-900"
                />
              ) : null}
              {reportId ? (
                <Chip variant="primary">
                  <BadgeCheck className="mr-1 h-3 w-3" aria-hidden />
                  Report: {relationship?.playbookName || 'View'}
                </Chip>
              ) : null}
            </div>
          )}

          {/* Likelihood score strip */}
          {hasScores ? (
            <StatRow
              stats={[
                { icon: Target, value: formatPercentValue(contact.relevanceLikelihood), label: 'Relevance' },
                { icon: Sparkles, value: formatPercentValue(contact.responseLikelihood), label: 'Response' },
                { icon: BadgeCheck, value: formatPercentValue(contact.hiringImpactLikelihood), label: 'Impact' },
              ]}
              className="w-full"
            />
          ) : null}

          {dataError ? (
            <HintNote
              tone="default"
              className="border-destructive/40 bg-destructive/10 text-destructive"
            >
              {dataError}
            </HintNote>
          ) : null}

          {/* Profile sections (collapsible) */}
          <div className="space-y-2">
            <SectionPanel title="Summary" collapsible defaultOpen={false}>
              {profileData?.headline || profileData?.summary ? (
                <div className="space-y-3 px-3 py-3 text-body-sm sm:px-4">
                  {profileData?.headline ? (
                    <div>
                      <p className="text-overline text-muted-foreground">Headline</p>
                      <p className="mt-0.5 text-foreground">{profileData.headline}</p>
                    </div>
                  ) : null}
                  {profileData?.summary ? (
                    <div>
                      <p className="text-overline text-muted-foreground">Summary</p>
                      <p className="mt-0.5 whitespace-pre-wrap text-foreground">{profileData.summary}</p>
                    </div>
                  ) : null}
                </div>
              ) : (
                <PlaceholderSection title="No summary" description="No headline or bio available for this contact." />
              )}
            </SectionPanel>

            <SectionPanel title={`Contact Channels (${channelCount})`} icon={Mail} collapsible defaultOpen={false}>
              {channelCount === 0 ? (
                <PlaceholderSection title="No channels" description="No emails, phones, or links on file yet." />
              ) : (
                <div className="grid gap-3 px-3 py-3 text-body-sm sm:px-4 md:grid-cols-3">
                  <div className="space-y-1.5">
                    <p className="text-overline text-muted-foreground">Emails</p>
                    {profileEmails.length === 0 ? (
                      <p className="text-muted-foreground">None</p>
                    ) : (
                      <div className="space-y-1">
                        {profileEmails.map((email) => (
                          <EmailCopy key={email} email={email} textClassName="text-body-sm text-foreground" iconClassName="h-3 w-3" />
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-overline text-muted-foreground">Phones</p>
                    {profilePhones.length === 0 ? (
                      <p className="text-muted-foreground">None</p>
                    ) : (
                      <div className="space-y-1">
                        {profilePhones.map((phone) => (
                          <IconLabel key={phone} icon={Phone} size="md" tone="foreground">{phone}</IconLabel>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-overline text-muted-foreground">Links</p>
                    {profileLinks.length === 0 ? (
                      <p className="text-muted-foreground">None</p>
                    ) : (
                      <div className="space-y-1">
                        {profileLinks.map((url) => (
                          <a key={url} href={url} target="_blank" rel="noreferrer" className="block break-all text-primary hover:underline">
                            {url}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </SectionPanel>

            <SectionPanel title={`Experience (${profileExperience.length})`} icon={Briefcase} collapsible defaultOpen={false}>
              {profileExperience.length === 0 ? (
                <PlaceholderSection title="No experience" description="No work history records." />
              ) : (
                <div className="divide-y divide-border">
                  {profileExperience.map((exp, idx) => {
                    const title = typeof exp.title === 'string' ? exp.title : 'Untitled role';
                    const company = typeof exp.company_name === 'string' ? exp.company_name : 'Unknown company';
                    const range = formatDateRange(
                      typeof exp.start_date === 'string' ? exp.start_date : null,
                      typeof exp.end_date === 'string' ? exp.end_date : null,
                      Boolean(exp.is_current),
                    );
                    const locality = typeof exp.locality === 'string' ? exp.locality : '';
                    return (
                      <div key={`${exp.id ?? idx}`} className="space-y-1 px-3 py-2.5 sm:px-4">
                        <EntityTitle as="p" size="sm">{title}</EntityTitle>
                        <EntitySubtitle tone="muted">{company}</EntitySubtitle>
                        <MetaRow size="sm">
                          <span>{range}</span>
                          {locality ? <span>{locality}</span> : null}
                        </MetaRow>
                        {typeof exp.summary === 'string' && exp.summary ? (
                          <p className="whitespace-pre-wrap text-body-sm text-foreground">{exp.summary}</p>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </SectionPanel>

            <SectionPanel title={`Education (${profileEducation.length})`} icon={GraduationCap} collapsible defaultOpen={false}>
              {profileEducation.length === 0 ? (
                <PlaceholderSection title="No education" description="No academic records on file." />
              ) : (
                <div className="divide-y divide-border">
                  {profileEducation.map((edu, idx) => {
                    const school = typeof edu.school_name === 'string' ? edu.school_name : typeof edu.school === 'string' ? edu.school : 'School';
                    const degree = typeof edu.degree === 'string' ? edu.degree : 'Degree not provided';
                    const field = typeof edu.field_of_study === 'string' ? edu.field_of_study : '';
                    return (
                      <div key={`${edu.id ?? idx}`} className="space-y-0.5 px-3 py-2.5 sm:px-4">
                        <EntityTitle as="p" size="sm">{school}</EntityTitle>
                        <EntitySubtitle tone="muted">
                          {degree}{field ? ` · ${field}` : ''}
                        </EntitySubtitle>
                      </div>
                    );
                  })}
                </div>
              )}
            </SectionPanel>

            {profileOtherLists.map((section) => (
              <SectionPanel
                key={section.key}
                title={`${section.key.replace(/_/g, ' ')} (${section.values.length})`}
                collapsible
                defaultOpen={false}
              >
                <div className="divide-y divide-border">
                  {section.values.map((entry, idx) => (
                    <div key={idx} className="px-3 py-2 text-body-sm sm:px-4">
                      {entry && typeof entry === 'object' ? (
                        <SimpleKeyValueTable
                          rows={Object.entries(entry as GenericRecord).map(([key, value]) => ({
                            label: key.replace(/_/g, ' '),
                            value: renderRecordValue(value),
                          }))}
                        />
                      ) : (
                        <p className="break-all text-foreground">{renderRecordValue(entry)}</p>
                      )}
                    </div>
                  ))}
                </div>
              </SectionPanel>
            ))}

            {profileRawFields.length > 0 ? (
              <SectionPanel title={`Additional Fields (${profileRawFields.length})`} collapsible defaultOpen={false}>
                <SimpleKeyValueTable
                  rows={profileRawFields.map(([key, value]) => ({
                    label: key.replace(/_/g, ' '),
                    value: renderRecordValue(value),
                  }))}
                />
              </SectionPanel>
            ) : null}

            {/* Engagement, Notes, Tasks, Interviews — actionable sections */}
            <SectionPanel title="Engagement History" icon={Calendar} collapsible defaultOpen={false}>
              {isLoading ? (
                <PlaceholderSection title="Loading…" description="Fetching outreach history." />
              ) : engagementMessages.length === 0 ? (
                <PlaceholderSection
                  icon={<Calendar className="h-6 w-6" />}
                  title="No outreach yet"
                  description="Past messages with this contact will appear here."
                />
              ) : (
                <div className="divide-y divide-border">
                  {engagementMessages.map((msg) => (
                    <div key={msg.id} className="space-y-1 px-3 py-2.5 sm:px-4">
                      <div className="flex items-center justify-between">
                        <span className="text-overline text-muted-foreground">
                          {msg.direction === 'INBOUND' ? 'From contact' : 'You'}
                        </span>
                        <span className="text-caption text-muted-foreground">{formatDateUs(msg.created_at)}</span>
                      </div>
                      <p className="text-body-sm text-foreground">
                        {msg.subject ? <strong>{msg.subject}: </strong> : null}
                        {msg.content}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </SectionPanel>

            <SectionPanel
              title={`Notes (${noteMessages.length})`}
              icon={StickyNote}
              trailing={addButton(() => setIsAddNoteOpen(true))}
            >
              {noteMessages.length === 0 ? (
                <PlaceholderSection
                  icon={<StickyNote className="h-6 w-6" />}
                  title="No notes yet"
                  description="Capture observations from calls, intel, or meetings."
                />
              ) : (
                <div className="divide-y divide-border">
                  {noteMessages.map((msg) => (
                    <div key={msg.id} className="space-y-1 px-3 py-2.5 sm:px-4">
                      <div className="flex items-center justify-between text-caption text-muted-foreground">
                        <span>{formatDateUs(msg.created_at)}</span>
                        <div className="flex items-center gap-0.5">
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => handleStartEditNote(msg.id, msg.content)} disabled={deletingNoteId === msg.id} aria-label="Edit note">
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-destructive" onClick={() => handleDeleteNote(msg.id)} disabled={deletingNoteId === msg.id} aria-label="Delete note">
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      {editingNoteId === msg.id ? (
                        <div className="space-y-2">
                          <Textarea value={editingNoteContent} onChange={(e) => setEditingNoteContent(e.target.value)} className="min-h-[100px]" />
                          <div className="flex gap-2">
                            <Button size="sm" className="h-7" onClick={handleSaveNoteEdit} disabled={isSavingNoteEdit || !editingNoteContent.trim()}>
                              {isSavingNoteEdit ? 'Saving…' : 'Save'}
                            </Button>
                            <Button size="sm" variant="outline" className="h-7" onClick={handleCancelEditNote} disabled={isSavingNoteEdit}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-body-sm text-foreground">{msg.content}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </SectionPanel>

            <SectionPanel
              title={`Tasks (${tasks.length})`}
              icon={CheckSquare}
              trailing={addButton(() => setIsAddTaskOpen(true))}
            >
              {tasks.length === 0 ? (
                <PlaceholderSection
                  icon={<CheckSquare className="h-6 w-6" />}
                  title="No tasks yet"
                  description="Track follow-ups, deliverables, and reminders here."
                />
              ) : (
                <div className="divide-y divide-border">
                  {tasks.map((task) => (
                    <div key={task.id} className="space-y-0.5 px-3 py-2.5 sm:px-4">
                      <div className="flex items-center justify-between gap-2">
                        <EntityTitle as="p" size="sm">{task.title}</EntityTitle>
                        {task.dueDate ? (
                          <span className="shrink-0 text-caption text-muted-foreground">{formatDateUs(task.dueDate)}</span>
                        ) : null}
                      </div>
                      {task.description ? (
                        <p className="text-body-sm text-muted-foreground">{task.description}</p>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </SectionPanel>

            <SectionPanel
              title={`Interviews (${interviews.length})`}
              icon={FileText}
              trailing={addButton(() => setIsScheduleInterviewOpen(true))}
            >
              {interviews.length === 0 ? (
                <PlaceholderSection
                  icon={<FileText className="h-6 w-6" />}
                  title="No interviews scheduled"
                  description="Schedule an interview to track meetings with this contact."
                />
              ) : (
                <div className="divide-y divide-border">
                  {interviews.map((interview) => (
                    <div key={interview.id} className="space-y-0.5 px-3 py-2.5 sm:px-4">
                      <div className="flex items-center justify-between gap-2">
                        <EntityTitle as="p" size="sm">{interview.type}</EntityTitle>
                        {interview.scheduledAt ? (
                          <span className="shrink-0 text-caption text-muted-foreground">{formatDateUs(interview.scheduledAt)}</span>
                        ) : null}
                      </div>
                      {interview.notes ? (
                        <p className="text-body-sm text-muted-foreground">{interview.notes}</p>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </SectionPanel>
          </div>
        </div>
      </PanelDialog>

      <AddNoteDialog open={isAddNoteOpen} onOpenChange={setIsAddNoteOpen} onSave={handleSaveNote} contactName={contact.fullName} />
      <AddTaskDialog open={isAddTaskOpen} onOpenChange={setIsAddTaskOpen} onSave={handleSaveTask} contactName={contact.fullName} />
      <ScheduleInterviewDialog open={isScheduleInterviewOpen} onOpenChange={setIsScheduleInterviewOpen} onSave={handleScheduleInterview} contactName={contact.fullName} companyName={contact.company} />
    </>
  );
}
